import { useState, useEffect, useCallback } from 'react';
import { 
  getActivitiesApi,
  getConsignmentsApi,
  getCustomerOrdersApi,
  getHumanMeansApi,
  getInvoicingDocumentsApi,
  getItemsApi,
  getMaterialMeansApi,
  getPiecesApi,
  getProductsApi,
  getQuotationRequestsApi, 
  getQuotationsApi, 
  getServiceRequestsApi,
  getServicesApi, 
  getHostsApi, 
  getMembersApi
} from '@/services/bolApiClient';
import { getSubscriptionsApi, getConsumersApi } from '@/services/bolNotificationsApiClient';

// Helper function to extract error message (DRY)
const getErrorMessage = (err: unknown, defaultMsg: string): string => {
  return err instanceof Error ? err.message : defaultMsg;
};

type CountResponse = {
  pagination?: { total?: number };
};

export interface DashboardStats {
  // BOL Stats
  hosts: number;
  members: number;
  activities: number;
  consignments: number;
  customerOrders: number;
  humanMeans: number;
  invoicingDocuments: number;
  items: number;
  materialMeans: number;
  pieces: number;
  products: number;
  quotationRequests: number;
  quotations: number;
  serviceRequests: number;
  services: number;
  // Notifications Stats
  subscriptions: number;
  totalConsumers: number;
}

export type DashboardStatKey = keyof DashboardStats;
export type DashboardStatErrors = Partial<Record<DashboardStatKey, string>>;

interface DashboardStatRequest {
  key: DashboardStatKey;
  request: () => Promise<CountResponse>;
  defaultErrorMessage: string;
}

export interface UseDashboardProps {
  canReadHost?: boolean;
  canReadMember?: boolean;
  canReadLogisticsObject?: boolean;
  canReadSubscription?: boolean;
}

export interface UseDashboardReturn {
  stats: DashboardStats;
  statErrors: DashboardStatErrors;
  // BOL Loading states
  bolLogisticsLoading: boolean;
  bolManagementLoading: boolean;
  bolLogisticsError: string | null;
  bolManagementError: string | null;
  // BOL Notifications Loading states
  notificationsLoading: boolean;
  notificationsError: string | null;
  // Methods
  refreshStats: () => void;
}

export const useDashboard = ({ 
  canReadHost = false,
  canReadMember = false,
  canReadLogisticsObject = false,
  canReadSubscription = false
}: UseDashboardProps): UseDashboardReturn => {
  // BOL Loading states
  const [bolLogisticsLoading, setBolLogisticsLoading] = useState(true);
  const [bolManagementLoading, setBolManagementLoading] = useState(true);
  const [bolLogisticsError, setBolLogisticsError] = useState<string | null>(null);
  const [bolManagementError, setBolManagementError] = useState<string | null>(null);
  
  // BOL Notifications Loading states
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [notificationsError, setNotificationsError] = useState<string | null>(null);
  const [statErrors, setStatErrors] = useState<DashboardStatErrors>({});
  
  const [stats, setStats] = useState<DashboardStats>({
    // BOL Stats
    hosts: 0,
    members: 0,
    activities: 0,
    consignments: 0,
    customerOrders: 0,
    humanMeans: 0,
    invoicingDocuments: 0,
    items: 0,
    materialMeans: 0,
    pieces: 0,
    products: 0,
    quotationRequests: 0,
    quotations: 0,
    serviceRequests: 0,
    services: 0,
    // Notifications Stats
    subscriptions: 0,
    totalConsumers: 0,
  });

  const clearStatErrors = useCallback((keys: DashboardStatKey[]) => {
    setStatErrors(prev => {
      const next = { ...prev };

      keys.forEach(key => {
        delete next[key];
      });

      return next;
    });
  }, []);

  const updateStatErrors = useCallback((nextErrors: DashboardStatErrors, keysToClear: DashboardStatKey[]) => {
    setStatErrors(prev => {
      const next = { ...prev };

      keysToClear.forEach(key => {
        delete next[key];
      });

      Object.entries(nextErrors).forEach(([key, error]) => {
        if (error) {
          next[key as DashboardStatKey] = error;
        }
      });

      return next;
    });
  }, []);

  // BOL Management Stats (Hosts and Members)
  const fetchBolManagementStats = useCallback(async () => {
    const requests: DashboardStatRequest[] = [
      ...(canReadHost
        ? [{
            key: 'hosts' as const,
            request: () => getHostsApi().listHosts({ limit: 1, offset: 0 }),
            defaultErrorMessage: 'Failed to load hosts count',
          }]
        : []),
      ...(canReadMember
        ? [{
            key: 'members' as const,
            request: () => getMembersApi().listMembers({ limit: 1, offset: 0 }),
            defaultErrorMessage: 'Failed to load members count',
          }]
        : []),
    ];

    if (requests.length === 0) {
      setBolManagementLoading(false);
      return;
    }
    
    setBolManagementLoading(true);
    setBolManagementError(null);
    clearStatErrors(requests.map(({ key }) => key));

    const results = await Promise.allSettled(requests.map(({ request }) => request()));
    const nextStats: Partial<DashboardStats> = {};
    const nextErrors: DashboardStatErrors = {};

    results.forEach((result, index) => {
      const { key, defaultErrorMessage } = requests[index];

      if (result.status === 'fulfilled') {
        nextStats[key] = result.value.pagination?.total || 0;
        return;
      }

      nextErrors[key] = getErrorMessage(result.reason, defaultErrorMessage);
    });

    if (Object.keys(nextStats).length > 0) {
      setStats(prev => ({
        ...prev,
        ...nextStats,
      }));
    }

    updateStatErrors(nextErrors, requests.map(({ key }) => key));
    setBolManagementError(results.every(result => result.status === 'rejected') ? 'Failed to load BOL management statistics' : null);
    setBolManagementLoading(false);
  }, [canReadHost, canReadMember, clearStatErrors, updateStatErrors]);

  // BOL Logistics Stats (all logistics object types)
  const fetchBolLogisticsStats = useCallback(async () => {
    if (!canReadLogisticsObject) {
      setBolLogisticsLoading(false);
      return;
    }
    
    setBolLogisticsLoading(true);
    setBolLogisticsError(null);
    const requests: DashboardStatRequest[] = [
      {
        key: 'activities',
        request: () => getActivitiesApi().searchActivitiesPost({
          contentType: 'application/json' as const,
          searchActivitiesRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load activities count',
      },
      {
        key: 'consignments',
        request: () => getConsignmentsApi().searchConsignmentsPost({
          contentType: 'application/json' as const,
          searchConsignmentsRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load consignments count',
      },
      {
        key: 'customerOrders',
        request: () => getCustomerOrdersApi().searchCustomerOrdersPost({
          contentType: 'application/json' as const,
          searchCustomerOrdersRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load customer orders count',
      },
      {
        key: 'humanMeans',
        request: () => getHumanMeansApi().searchHumanMeansPost({
          contentType: 'application/json' as const,
          searchHumanMeansRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load human means count',
      },
      {
        key: 'invoicingDocuments',
        request: () => getInvoicingDocumentsApi().searchInvoicingDocumentsPost({
          contentType: 'application/json' as const,
          searchInvoicingDocumentsRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load invoicing documents count',
      },
      {
        key: 'items',
        request: () => getItemsApi().searchItemsPost({
          contentType: 'application/json' as const,
          searchItemsRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load items count',
      },
      {
        key: 'materialMeans',
        request: () => getMaterialMeansApi().searchMaterialMeansPost({
          contentType: 'application/json' as const,
          searchMaterialMeansRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load material means count',
      },
      {
        key: 'pieces',
        request: () => getPiecesApi().searchPiecesPost({
          contentType: 'application/json' as const,
          searchPiecesRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load pieces count',
      },
      {
        key: 'products',
        request: () => getProductsApi().searchProductsPost({
          contentType: 'application/json' as const,
          searchProductsRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load products count',
      },
      {
        key: 'quotationRequests',
        request: () => getQuotationRequestsApi().searchQuotationRequestsPost({
          contentType: 'application/json' as const,
          searchQuotationRequestsRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load quotation requests count',
      },
      {
        key: 'quotations',
        request: () => getQuotationsApi().searchQuotationsPost({
          contentType: 'application/json' as const,
          searchQuotationsRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load quotations count',
      },
      {
        key: 'serviceRequests',
        request: () => getServiceRequestsApi().searchServiceRequestsPost({
          contentType: 'application/json' as const,
          searchServiceRequestsRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load service requests count',
      },
      {
        key: 'services',
        request: () => getServicesApi().searchServicesPost({
          contentType: 'application/json' as const,
          searchServicesRequest: {},
          limit: 1,
          offset: 0,
        }),
        defaultErrorMessage: 'Failed to load services count',
      },
    ];

    clearStatErrors(requests.map(({ key }) => key));

    const results = await Promise.allSettled(requests.map(({ request }) => request()));
    const nextStats: Partial<DashboardStats> = {};
    const nextErrors: DashboardStatErrors = {};

    results.forEach((result, index) => {
      const { key, defaultErrorMessage } = requests[index];

      if (result.status === 'fulfilled') {
        nextStats[key] = result.value.pagination?.total || 0;
        return;
      }

      nextErrors[key] = getErrorMessage(result.reason, defaultErrorMessage);
    });

    if (Object.keys(nextStats).length > 0) {
      setStats(prev => ({
        ...prev,
        ...nextStats,
      }));
    }

    updateStatErrors(nextErrors, requests.map(({ key }) => key));
    setBolLogisticsError(results.every(result => result.status === 'rejected') ? 'Failed to load BOL logistics statistics' : null);
    setBolLogisticsLoading(false);
  }, [canReadLogisticsObject, clearStatErrors, updateStatErrors]);

  // BOL Notifications Stats
  const fetchNotificationsStats = useCallback(async () => {
    if (!canReadSubscription) {
      setNotificationsLoading(false);
      return;
    }
    
    setNotificationsLoading(true);
    setNotificationsError(null);
    clearStatErrors(['subscriptions', 'totalConsumers']);
    try {
      const subscriptionsApi = getSubscriptionsApi();
      const consumersApi = getConsumersApi();
      
      // Get all subscriptions to count total consumers
      const subscriptionsRes = await subscriptionsApi.listSubscriptions({
        limit: 100,
        offset: 0
      });
      
      let totalConsumers = 0;
      
      // Fetch consumers for each subscription and sum them up
      const consumerPromises = subscriptionsRes.results.map(async (subscription) => {
        try {
          const consumersRes = await consumersApi.listConsumers({
            subscriptionId: subscription.id,
            limit: 1,
            offset: 0
          });
          return consumersRes.pagination.total;
        } catch {
          return 0; // If we can't fetch consumers for a subscription, count as 0
        }
      });
      
      const consumerCounts = await Promise.all(consumerPromises);
      totalConsumers = consumerCounts.reduce((sum, count) => sum + count, 0);
      
      setStats(prev => ({
        ...prev,
        subscriptions: subscriptionsRes.pagination.total,
        totalConsumers,
      }));
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err, 'Failed to load notifications statistics');
      updateStatErrors(
        {
          subscriptions: errorMessage,
          totalConsumers: errorMessage,
        },
        ['subscriptions', 'totalConsumers']
      );
      setNotificationsError(errorMessage);
    } finally {
      setNotificationsLoading(false);
    }
  }, [canReadSubscription, clearStatErrors, updateStatErrors]);

  const refreshStats = useCallback(() => {
    fetchBolManagementStats();
    fetchBolLogisticsStats();
    fetchNotificationsStats();
  }, [
    fetchBolManagementStats,
    fetchBolLogisticsStats,
    fetchNotificationsStats
  ]);

  useEffect(() => {
    refreshStats();
  }, [refreshStats]);

  return {
    stats,
    statErrors,
    bolLogisticsLoading,
    bolManagementLoading,
    bolLogisticsError,
    bolManagementError,
    notificationsLoading,
    notificationsError,
    refreshStats
  };
};